import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import api from '@/api';
import {
  ArrowLeft, Building2, Calendar, FolderOpen, FileText, AlertTriangle,
  CheckCircle2, Clock, Plus, Upload, ChevronDown, ChevronRight,
  Loader2, BarChart3, X, Shield, Mail, Wand2, Users, Link2, Save, Package, Inbox,
  Download, FileSpreadsheet, FileArchive, Trash2, ClipboardCheck, FileCheck2, Layers,
  Search, FileSearch, Truck, Phone, Route, Eye, Sparkles
} from 'lucide-react';
import G703ImportModal from './G703ImportModal';
import ApplicabilityWizard from './ApplicabilityWizard';
import WarrantyPanel from './WarrantyPanel';
import ChaseEmailPanel from './ChaseEmailPanel';
import DocumentUploadPanel from './DocumentUploadPanel';
import StakeholderPanel from './StakeholderPanel';
import QualityGates from './QualityGates';
import WarrantyCalendar from './WarrantyCalendar';
import ProcoreSyncPanel from './ProcoreSyncPanel';
import AssetPanel from './AssetPanel';
import IntakePanel from './IntakePanel';
import AuditPanel from './AuditPanel';
import TimelinePanel from './TimelinePanel';
import TeamPanel, { RoleBadge } from './TeamPanel';
import FloorPlanPanel from './FloorPlanPanel';
import SubmittalPanel from './SubmittalPanel';
import DigestPreferences from './DigestPreferences';
import EmailGuideModal from './EmailGuideModal';
import DrawingSetsPanel from './DrawingSetsPanel';
import TradePortalManager from './TradePortalManager';
import HandoverReviewModal from './HandoverReviewModal';
import ServiceContactsPanel from './ServiceContactsPanel';

// ─── Workflow Stepper Component ─────────────────────────────────────────
const STEP_ICONS = {
  ingest: Upload,
  scan: Search,
  chase: AlertTriangle,
  submittals: FileCheck2,
  docs: FileSearch,
  handover: Truck,
};

const STEP_TAB_MAP = {
  ingest: 'intake',
  scan: 'assets',
  chase: 'assets',
  submittals: 'submittals',
  docs: 'warranties',
  handover: null,
};

function WorkflowStepper({ projectId, onStepClick, refreshKey }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/closeout/projects/${projectId}/workflow-status`)
      .then(r => setData(r.data))
      .catch(() => {});
  }, [projectId, refreshKey]);

  if (!data) return null;

  return (
    <div className="mb-6" data-testid="workflow-stepper">
      {/* Overall progress line */}
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] font-heading uppercase tracking-widest text-muted-foreground">
          Project Workflow
        </span>
        <span className="text-[10px] font-mono text-muted-foreground">
          {data.done_count}/{data.total_steps} complete — <span className={data.overall_pct >= 80 ? 'text-emerald-600 dark:text-emerald-400' : 'text-foreground'}>{data.overall_pct}%</span>
        </span>
      </div>

      {/* Step cards */}
      <div className="flex gap-1">
        {data.steps.map((step, idx) => {
          const Icon = STEP_ICONS[step.key] || Package;
          const isComplete = step.done;
          const isPartial = !isComplete && step.pct > 0;
          const tabTarget = STEP_TAB_MAP[step.key];

          return (
            <button
              key={step.key}
              onClick={() => tabTarget && onStepClick(tabTarget, step.key)}
              data-testid={`workflow-step-${step.key}`}
              className={`flex-1 relative overflow-hidden rounded-sm border px-2 py-2.5 text-center transition-all group ${
                isComplete
                  ? 'bg-emerald-50 dark:bg-emerald-950/20 border-emerald-300 dark:border-emerald-800/40 hover:border-emerald-500 dark:hover:border-emerald-600'
                  : isPartial
                  ? 'bg-amber-50/50 dark:bg-amber-950/10 border-amber-200 dark:border-amber-900/30 hover:border-amber-400 dark:hover:border-amber-700'
                  : 'bg-card border-border hover:border-muted-foreground/30'
              } ${tabTarget ? 'cursor-pointer' : 'cursor-default'}`}
            >
              {/* Progress bar background */}
              {isPartial && (
                <div className="absolute inset-0 bg-amber-100/30 dark:bg-amber-900/10"
                  style={{ width: `${step.pct}%` }} />
              )}
              <div className="relative z-10">
                <div className="flex items-center justify-center gap-1 mb-1">
                  {isComplete ? (
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
                  ) : (
                    <Icon className={`w-3.5 h-3.5 ${isPartial ? 'text-amber-600 dark:text-amber-400' : 'text-muted-foreground'}`} />
                  )}
                </div>
                <p className={`text-[10px] font-heading uppercase tracking-wider leading-tight ${
                  isComplete ? 'text-emerald-700 dark:text-emerald-400' :
                  isPartial ? 'text-amber-700 dark:text-amber-400' : 'text-muted-foreground'
                }`}>
                  {step.label}
                </p>
                <p className="text-[9px] text-muted-foreground mt-0.5 truncate">{step.detail}</p>
              </div>
              {/* Connector arrow */}
              {idx < data.steps.length - 1 && (
                <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 z-20 w-3 h-3 text-border">
                  <ChevronRight className="w-3 h-3" />
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

const FOLDER_NAMES = {
  A: 'O&M Data', B: 'Warranties', C: 'As-Builts', D: 'Attic Stock',
  E: 'Training', F: 'Cx / TAB', G: 'Service Contracts', H: 'Submittals', I: 'Other',
};

const applicabilityColors = {
  required: 'bg-blue-100 dark:bg-blue-950/30 text-blue-700 dark:text-blue-400 border-blue-300 dark:border-blue-900',
  conditional: 'bg-amber-100 dark:bg-amber-950/30 text-amber-700 dark:text-amber-400 border-amber-300 dark:border-amber-900',
  not_applicable: 'bg-muted/50 text-muted-foreground border-border',
  undecided: 'bg-red-100 dark:bg-red-950/20 text-red-700 dark:text-red-400 border-red-300 dark:border-red-900/50',
};

// ─── Extraction History & Export History Panel ──────────────────────────
function ExtractionHistoryPanel({ projectId }) {
  const [batches, setBatches] = useState([]);
  const [exportHistory, setExportHistory] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [diffs, setDiffs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get(`/closeout/projects/${projectId}/extraction-diffs`).then(r => setBatches(r.data || [])).catch(() => {}),
      api.get(`/closeout/projects/${projectId}/export-history`).then(r => setExportHistory(r.data || [])).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, [projectId]);

  const loadDiff = async (batchId) => {
    if (selectedBatch === batchId) { setSelectedBatch(null); setDiffs([]); return; }
    setSelectedBatch(batchId);
    try {
      const r = await api.get(`/closeout/projects/${projectId}/extraction-diff/${batchId}`);
      setDiffs(r.data || []);
    } catch { setDiffs([]); }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-40">
      <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
    </div>
  );

  return (
    <div className="space-y-6" data-testid="extraction-history-panel">
      {/* Extraction Diff Report */}
      <div>
        <h3 className="text-xs font-heading uppercase tracking-widest text-primary/80 mb-3 flex items-center gap-2">
          <Search className="w-4 h-4" /> Extraction Diff Report
        </h3>
        {batches.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-28 bg-card border border-dashed border-border rounded-sm">
            <Search className="w-6 h-6 text-muted-foreground/40 mb-1" />
            <p className="text-[10px] text-muted-foreground">No extraction diffs recorded yet</p>
            <p className="text-[9px] text-muted-foreground/60 mt-0.5">Run a re-extraction on an existing drawing set to see change diffs</p>
          </div>
        ) : (
          <div className="space-y-2">
            {batches.map(b => (
              <div key={b.batch_id} className="border border-border rounded-sm bg-card">
                <button onClick={() => loadDiff(b.batch_id)}
                  data-testid={`diff-batch-${b.batch_id}`}
                  className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted/20 transition-colors text-left">
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-mono text-muted-foreground">
                      {new Date(b.created_at).toLocaleString()}
                    </span>
                    <div className="flex items-center gap-2">
                      {b.created > 0 && (
                        <span className="text-[9px] font-mono px-1.5 py-0.5 rounded-sm bg-emerald-100 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-400 border border-emerald-300 dark:border-emerald-800/30">
                          {b.created} new
                        </span>
                      )}
                      {b.enriched > 0 && (
                        <span className="text-[9px] font-mono px-1.5 py-0.5 rounded-sm bg-blue-100 dark:bg-blue-950/30 text-blue-700 dark:text-blue-400 border border-blue-300 dark:border-blue-800/30">
                          {b.enriched} enriched
                        </span>
                      )}
                      {b.skipped_verified > 0 && (
                        <span className="text-[9px] font-mono px-1.5 py-0.5 rounded-sm bg-slate-100 dark:bg-slate-900/30 text-slate-600 dark:text-slate-400 border border-slate-300 dark:border-slate-700/30">
                          {b.skipped_verified} verified-locked
                        </span>
                      )}
                    </div>
                  </div>
                  <ChevronDown className={`w-3.5 h-3.5 text-muted-foreground transition-transform ${selectedBatch === b.batch_id ? 'rotate-180' : ''}`} />
                </button>
                {selectedBatch === b.batch_id && diffs.length > 0 && (
                  <div className="border-t border-border/50 divide-y divide-border/30 max-h-[400px] overflow-y-auto">
                    {diffs.map(d => (
                      <div key={d.id} className={`px-4 py-2.5 text-xs ${
                        d.action === 'enriched' ? 'bg-blue-50/30 dark:bg-blue-950/5' :
                        d.action === 'skipped_verified' ? 'bg-slate-50/30 dark:bg-slate-950/5' : ''
                      }`} data-testid={`diff-${d.id}`}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium text-foreground/80">{d.asset_name}</span>
                          <div className="flex items-center gap-1.5">
                            <span className={`text-[9px] font-mono px-1 py-0.5 rounded-sm ${
                              d.action === 'enriched' ? 'bg-blue-100 dark:bg-blue-950/30 text-blue-600 dark:text-blue-400' :
                              d.action === 'skipped_verified' ? 'bg-slate-100 dark:bg-slate-900/30 text-slate-500' :
                              'bg-emerald-100 dark:bg-emerald-950/30 text-emerald-600'
                            }`}>{d.action}</span>
                            <span className="text-[9px] text-muted-foreground/60">via {d.match_method}</span>
                          </div>
                        </div>
                        {d.action === 'enriched' && Object.keys(d.changed_fields || {}).length > 0 && (
                          <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 mt-1 text-[10px]">
                            {Object.entries(d.changed_fields).map(([k, newVal]) => (
                              <div key={k} className="flex gap-1 font-mono">
                                <span className="text-muted-foreground w-24 shrink-0">{k}:</span>
                                <span className="text-red-400 line-through mr-1">
                                  {typeof d.previous_values?.[k] === 'object' ? JSON.stringify(d.previous_values[k]) : (d.previous_values?.[k] || '(empty)')}
                                </span>
                                <span className="text-emerald-600 dark:text-emerald-400">
                                  {typeof newVal === 'object' ? JSON.stringify(newVal) : newVal}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                        {d.action === 'skipped_verified' && (
                          <p className="text-[10px] text-muted-foreground italic">Skipped — user-verified asset locked from changes</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {selectedBatch === b.batch_id && diffs.length === 0 && (
                  <div className="px-4 py-4 text-center text-[10px] text-muted-foreground border-t border-border/50">
                    No changes recorded for this batch
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Export History */}
      <div>
        <h3 className="text-xs font-heading uppercase tracking-widest text-primary/80 mb-3 flex items-center gap-2">
          <Download className="w-4 h-4" /> Chase Export & Import History
        </h3>
        {exportHistory.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-28 bg-card border border-dashed border-border rounded-sm">
            <Download className="w-6 h-6 text-muted-foreground/40 mb-1" />
            <p className="text-[10px] text-muted-foreground">No export or import activity yet</p>
          </div>
        ) : (
          <div className="space-y-1.5">
            {exportHistory.map(h => (
              <div key={h.id} className={`flex items-center gap-3 px-4 py-2.5 border rounded-sm ${
                h.action === 'chase_export'
                  ? 'border-blue-200 dark:border-blue-800/30 bg-blue-50/30 dark:bg-blue-950/5'
                  : 'border-emerald-200 dark:border-emerald-800/30 bg-emerald-50/30 dark:bg-emerald-950/5'
              }`} data-testid={`history-${h.id}`}>
                {h.action === 'chase_export' ? (
                  <Download className="w-4 h-4 text-blue-500 shrink-0" />
                ) : (
                  <Upload className="w-4 h-4 text-emerald-500 shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-foreground/80">
                    {h.action === 'chase_export' ? 'Exported chase list' : 'Imported chase response'}
                    {h.filename && <span className="text-muted-foreground"> — {h.filename}</span>}
                  </p>
                  <div className="flex items-center gap-2 text-[10px] text-muted-foreground mt-0.5">
                    <span>{h.item_count} items</span>
                    <span>{h.user_email}</span>
                    {h.details?.promoted > 0 && (
                      <span className="text-emerald-600 dark:text-emerald-400">{h.details.promoted} promoted</span>
                    )}
                    {h.details?.group_names && (
                      <span className="truncate max-w-[200px]">{h.details.group_names.join(', ')}</span>
                    )}
                  </div>
                </div>
                <span className="text-[9px] font-mono text-muted-foreground shrink-0">
                  {new Date(h.created_at).toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function FolderCell({ folder, onClick }) {
  const hasDoc = folder.received_count > 0;
  const isNA = folder.applicability === 'not_applicable';
  const isUndecided = folder.applicability === 'undecided';
  let bgClass = 'bg-secondary/20 border-border hover:border-slate-600';
  let icon = <Clock className="w-3 h-3 text-muted-foreground" />;

  if (isNA) {
    bgClass = 'bg-muted/50 border-border/50 opacity-40';
    icon = <X className="w-3 h-3 text-muted-foreground" />;
  } else if (hasDoc) {
    bgClass = 'bg-emerald-100 dark:bg-emerald-950/30 border-emerald-300 dark:border-emerald-900/50 hover:border-emerald-500 dark:hover:border-emerald-700';
    icon = <CheckCircle2 className="w-3 h-3 text-emerald-600 dark:text-emerald-500" />;
  } else if (isUndecided) {
    bgClass = 'bg-red-50 dark:bg-red-950/10 border-red-300 dark:border-red-900/30 hover:border-red-500 dark:hover:border-red-700';
    icon = <AlertTriangle className="w-3 h-3 text-red-500/60" />;
  } else if (folder.document_count > 0) {
    bgClass = 'bg-amber-100 dark:bg-amber-950/20 border-amber-300 dark:border-amber-900/40 hover:border-amber-500 dark:hover:border-amber-700';
    icon = <Clock className="w-3 h-3 text-amber-500" />;
  }

  return (
    <button
      onClick={onClick}
      data-testid={`folder-cell-${folder.letter}`}
      className={`w-full flex items-center justify-center gap-1 px-1.5 py-1.5 rounded-sm border text-[10px] font-mono transition-colors ${bgClass}`}
      title={`${folder.letter} — ${FOLDER_NAMES[folder.letter] || folder.name}${isNA ? ' (N/A)' : ''}\nDocs: ${folder.document_count} | Received: ${folder.received_count}`}
    >
      {icon}
      <span className="text-muted-foreground">{folder.letter}</span>
      {folder.document_count > 0 && !isNA && (
        <span className="text-[9px] text-muted-foreground">{folder.received_count}/{folder.document_count}</span>
      )}
    </button>
  );
}

function DivisionRow({ div, projectCode, onRefresh }) {
  const [expanded, setExpanded] = useState(false);
  const isProjectLevel = div.is_project_level;

  return (
    <div className={`border rounded-sm transition-colors ${isProjectLevel ? 'border-primary/30 bg-blue-50 dark:bg-blue-950/10' : 'border-border bg-card'}`}>
      <div className="flex items-center gap-3 px-4 py-3">
        <button onClick={() => setExpanded(!expanded)} className="text-muted-foreground hover:text-foreground/80">
          {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>

        <span className="font-mono text-xs text-muted-foreground w-16 shrink-0">{div.csi_number}</span>
        <span className="text-sm text-foreground font-medium flex-1 truncate">
          {div.trade_name}
          {div.vendor_name && <span className="text-muted-foreground font-normal ml-2">({div.vendor_name})</span>}
          {div.source === 'ai_inferred' && (
            <span className="ml-2 text-[9px] font-mono px-1.5 py-0.5 rounded-sm bg-violet-100 dark:bg-violet-950/30 text-violet-700 dark:text-violet-400 border border-violet-300 dark:border-violet-800/40">
              AI-INFERRED
            </span>
          )}
        </span>

        <span className={`text-[10px] font-mono px-2 py-0.5 rounded-sm border ${applicabilityColors[div.applicability] || applicabilityColors.undecided}`}>
          {div.applicability?.toUpperCase()}
        </span>

        {/* Mini folder grid */}
        <div className="flex gap-1 shrink-0">
          {(div.folders || []).map(f => (
            <FolderCell key={f.letter} folder={f} onClick={() => setExpanded(true)} />
          ))}
        </div>

        <div className="w-14 text-right">
          <span className={`font-mono text-xs ${div.completion_pct >= 80 ? 'text-emerald-600 dark:text-emerald-400' : div.completion_pct > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-muted-foreground'}`}>
            {div.completion_pct}%
          </span>
        </div>
      </div>

      {expanded && (
        <div className="px-4 pb-4 pt-1 border-t border-border/50">
          <div className="grid grid-cols-3 gap-3">
            {(div.folders || []).map(f => (
              <div key={f.letter} className="bg-card border border-border rounded-sm p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-heading uppercase tracking-widest text-muted-foreground">
                    {f.letter} — {FOLDER_NAMES[f.letter] || f.name}
                  </span>
                  <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded-sm border ${applicabilityColors[f.applicability] || applicabilityColors.undecided}`}>
                    {f.applicability}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">
                    {f.received_count}/{f.document_count} received
                  </span>
                  {f.applicability !== 'not_applicable' && f.received_count === 0 && (
                    <span className="text-[10px] text-red-600 dark:text-red-400">Missing</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function CloseoutProject() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [divisions, setDivisions] = useState([]);
  const [gapReport, setGapReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showImport, setShowImport] = useState(false);
  const [showGapReport, setShowGapReport] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  const [showChaseEmails, setShowChaseEmails] = useState(false);
  const [showDocUpload, setShowDocUpload] = useState(false);
  const [showProcore, setShowProcore] = useState(false);
  const [activeTab, setActiveTab] = useState('intake');
  const [intakeSubTab, setIntakeSubTab] = useState('upload'); // 'upload' | 'timeline' | 'matrix' | 'drawingsets'
  const [warrantySubTab, setWarrantySubTab] = useState('documents'); // 'documents' | 'calendar'
  const [teamSubTab, setTeamSubTab] = useState('team'); // 'team' | 'stakeholders'
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [showMoreActions, setShowMoreActions] = useState(false);
  const [showDigestPrefs, setShowDigestPrefs] = useState(false);
  const [exportPreview, setExportPreview] = useState(null);
  const [exporting, setExporting] = useState(null);
  const [generatingHandover, setGeneratingHandover] = useState(false);
  const [searchParams] = useSearchParams();
  const [showEmailGuide, setShowEmailGuide] = useState(false);
  const [showEmailHandover, setShowEmailHandover] = useState(false);
  const [handoverReview, setHandoverReview] = useState(null); // 'generate' | 'email' | null
  const [previewing, setPreviewing] = useState(false);
  const [buildMsg, setBuildMsg] = useState(null);
  const [buildStatus, setBuildStatus] = useState(null);
  const [buildElapsed, setBuildElapsed] = useState(0);
  const buildStartRef = useRef(null);
  const [stepperKey, setStepperKey] = useState(0);
  const buildFileRef = useRef(null);

  useEffect(() => {
    if (!buildMsg) { setBuildElapsed(0); buildStartRef.current = null; return; }
    if (!buildStartRef.current) buildStartRef.current = Date.now();
    const t = setInterval(() => setBuildElapsed(Math.floor((Date.now() - buildStartRef.current) / 1000)), 1000);
    return () => clearInterval(t);
  }, [buildMsg]);

  useEffect(() => {
    const tabParam = searchParams.get('tab');
    const valid = ['intake', 'assets', 'submittals', 'floorplan', 'warranties', 'contacts', 'gates', 'audit', 'team'];
    if (tabParam && valid.includes(tabParam)) setActiveTab(tabParam);
  }, [searchParams]);

  const isOwner = !project || project.user_role === 'owner' || !project.user_role;

  const handleDeleteProject = async () => {
    if (!window.confirm(`Delete "${project?.project_name}"?\n\nThis will remove the project from your list. This action cannot be undone.`)) return;
    try {
      await api.delete(`/closeout/projects/${projectId}`);
      const { toast } = await import('sonner');
      toast.success('Project deleted');
      navigate('/closeout');
    } catch (err) {
      const { toast } = await import('sonner');
      toast.error(err.response?.data?.detail || 'Failed to delete project');
    }
  };

  const handleSaveAsTemplate = async () => {
    const name = window.prompt('Template name:', `${project?.project_name || 'Project'} Template`);
    if (!name) return;
    setSavingTemplate(true);
    try {
      await api.post(`/closeout/templates?project_id=${projectId}`, {
        name,
        description: `Saved from ${project?.project_name || projectId}`,
      });
      const { toast } = await import('sonner');
      toast.success(`Template "${name}" saved with ${divisions.length} divisions`);
    } catch (err) {
      console.error(err);
      const { toast } = await import('sonner');
      toast.error('Failed to save template');
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleGenerateHandover = async () => {
    setGeneratingHandover(true);
    try {
      const res = await api.post(`/closeout/projects/${projectId}/handover/generate`);
      const url = res.data.url;
      const { toast } = await import('sonner');
      toast.success('Handover package generated!');
      setStepperKey(k => k + 1);
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(url);
          toast.info('Link copied to clipboard');
        }
      } catch {
        /* clipboard may be blocked by browser permissions — link still opens below */
      }
      window.open(url, '_blank');
    } catch (err) {
      const { toast } = await import('sonner');
      toast.error(err.response?.data?.detail || 'Failed to generate handover');
    } finally {
      setGeneratingHandover(false);
    }
  };

  const handlePreviewHandover = async () => {
    setPreviewing(true);
    try {
      const res = await api.post(`/closeout/projects/${projectId}/handover/generate`);
      setStepperKey(k => k + 1);
      window.open(res.data.url, '_blank', 'noopener');
    } catch (err) {
      const { toast } = await import('sonner');
      toast.error(err.response?.data?.detail || 'Could not open preview');
    } finally {
      setPreviewing(false);
    }
  };

  const handleBuildFromDrawings = async (file) => {
    if (!file) return;
    const { toast } = await import('sonner');
    setBuildMsg('Uploading drawing set…');
    setBuildStatus({ stage: 'queued', message: 'Uploading drawing set…' });
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post(`/closeout/projects/${projectId}/build-register/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const buildId = res.data.build_id;
      const poll = setInterval(async () => {
        try {
          const s = await api.get(`/closeout/projects/${projectId}/build-register/status/${buildId}`);
          setBuildMsg(s.data.message);
          setBuildStatus(s.data);
          if (s.data.stage === 'complete' || s.data.stage === 'error') {
            clearInterval(poll);
            if (s.data.stage === 'complete') { toast.success(s.data.message); fetchData(); setStepperKey(k => k + 1); setActiveTab('assets'); }
            else { toast.error(s.data.message); }
            setTimeout(() => { setBuildMsg(null); setBuildStatus(null); }, 6000);
          }
        } catch { clearInterval(poll); setBuildMsg(null); setBuildStatus(null); }
      }, 2500);
    } catch (err) {
      setBuildMsg(null); setBuildStatus(null);
      toast.error(err?.response?.data?.detail || 'Failed to start register build');
    }
  };

  const fetchExportPreview = async () => {
    try {
      const res = await api.get(`/closeout/projects/${projectId}/export-preview`);
      setExportPreview(res.data);
      setShowExport(true);
    } catch (err) {
      console.error(err);
    }
  };

  const handleExport = async (format) => {
    setExporting(format);
    try {
      const res = await api.get(`/closeout/projects/${projectId}/export-handoff?format=${format}`, {
        responseType: 'blob',
      });
      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const code = project?.project_code || 'Project';
      const extensions = { pdf: 'pdf', excel: 'xlsx', zip: 'zip' };
      const names = { pdf: 'Building_Turnover_Report', excel: 'IWMS_Import', zip: 'Turnover_Package' };
      a.download = `${code}_${names[format]}.${extensions[format]}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
    } finally {
      setExporting(null);
    }
  };

  const fetchData = useCallback(async () => {
    try {
      const [pRes, dRes] = await Promise.all([
        api.get(`/closeout/projects/${projectId}`),
        api.get(`/closeout/projects/${projectId}/divisions`),
      ]);
      setProject(pRes.data);
      setDivisions(dRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const fetchGapReport = async () => {
    try {
      const res = await api.get(`/closeout/projects/${projectId}/gap-report`);
      setGapReport(res.data);
      setShowGapReport(true);
    } catch (err) {
      console.error(err);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );
  if (!project) return <div className="p-8 text-muted-foreground">Project not found</div>;

  const projectDivisions = divisions.filter(d => d.is_project_level);
  const tradeDivisions = divisions.filter(d => !d.is_project_level);

  return (
    <div className="p-8 max-w-7xl mx-auto" data-testid="closeout-project-detail">
      {/* Back */}
      <button onClick={() => navigate('/closeout')} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground/80 mb-4 transition-colors">
        <ArrowLeft className="w-3 h-3" /> All Activate Projects
      </button>

      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground tracking-tight">{project.project_name}</h1>
          <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
            <span className="font-mono text-primary">{project.project_code}</span>
            <RoleBadge role={project.user_role || 'owner'} />
            {project.building_name && <span className="flex items-center gap-1"><Building2 className="w-3 h-3" />{project.building_name}</span>}
            {project.gc_company && <span>{project.gc_company}</span>}
            {project.substantial_completion && (
              <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />SC: {project.substantial_completion}</span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {(project.user_role !== 'viewer') && (
            <>
            <input ref={buildFileRef} type="file" accept="application/pdf" className="hidden"
              data-testid="header-build-file"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) handleBuildFromDrawings(f); e.target.value = ''; }} />
            <button onClick={() => buildFileRef.current?.click()} disabled={!!buildMsg}
              data-testid="header-build-from-drawings-btn"
              className="flex items-center gap-2 px-3.5 py-2 text-xs font-heading uppercase tracking-wider bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors disabled:opacity-60">
              {buildMsg ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
              {buildMsg ? 'Building…' : 'Build from Drawings'}
            </button>
            </>
          )}
          {isOwner && (
            <button onClick={handlePreviewHandover} disabled={previewing}
              data-testid="preview-handover-btn"
              className="flex items-center gap-2 px-3.5 py-2 text-xs font-heading uppercase tracking-wider bg-emerald-600 hover:bg-emerald-700 text-white rounded-md transition-colors disabled:opacity-60">
              {previewing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Eye className="w-3.5 h-3.5" />}
              {previewing ? 'Opening…' : 'Preview Handover'}
            </button>
          )}

          {/* Overflow menu */}
          <div className="relative">
            <button onClick={() => setShowMoreActions(!showMoreActions)} data-testid="more-actions-btn"
              title="More actions" aria-label="More actions"
              className="flex items-center gap-1 px-2.5 py-2 text-xs text-muted-foreground hover:text-foreground border border-border hover:border-primary/30 rounded-md transition-colors">
              <ChevronDown className="w-3.5 h-3.5" />
            </button>
            {showMoreActions && (
              <div className="absolute right-0 top-full mt-1 w-52 bg-card border border-border rounded-sm shadow-lg z-20 max-h-[70vh] overflow-y-auto" data-testid="more-actions-menu">
                {(project.user_role !== 'viewer') && (
                  <>
                  <button onClick={() => { setShowImport(true); setShowMoreActions(false); }} data-testid="import-g703-btn"
                    className="w-full flex items-center gap-2 px-3 py-2 text-xs text-muted-foreground hover:text-foreground hover:bg-muted/30 transition-colors">
                    <Upload className="w-3.5 h-3.5" /> Import G703
                  </button>
                  <button onClick={() => { setShowWizard(true); setShowMoreActions(false); }} data-testid="applicability-wizard-btn"
                    className="w-full flex items-center gap-2 px-3 py-2 text-xs text-muted-foreground hover:text-foreground hover:bg-muted/30 transition-colors">
                    <Wand2 className="w-3.5 h-3.5" /> Applicability Wizard
                  </button>
                  <button onClick={() => { setShowProcore(true); setShowMoreActions(false); }} data-testid="procore-sync-btn"
                    className="w-full flex items-center gap-2 px-3 py-2 text-xs text-muted-foreground hover:text-foreground hover:bg-muted/30 transition-colors">
                    <Link2 className="w-3.5 h-3.5" /> Procore Sync
                  </button>
                  </>
                )}
                <button onClick={() => { fetchExportPreview(); setShowMoreActions(false); }} data-testid="export-handoff-btn"
                  className="w-full flex items-center gap-2 px-3 py-2 text-xs text-muted-foreground hover:text-foreground hover:bg-muted/30 transition-colors">
                  <Download className="w-3.5 h-3.5" /> Export Package
                </button>
                <div className="border-t border-border my-1" />
                {(project.user_role !== 'viewer') && (
                  <>
                  <button onClick={() => { setShowDocUpload(true); setShowMoreActions(false); }} data-testid="more-actions-upload-document"
                    className="w-full flex items-center gap-2 px-3 py-2 text-xs text-muted-foreground hover:text-foreground hover:bg-muted/30 transition-colors">
                    <Upload className="w-3.5 h-3.5" /> Upload Document
                  </button>
                  <button onClick={() => { setShowChaseEmails(true); setShowMoreActions(false); }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-xs text-muted-foreground hover:text-foreground hover:bg-muted/30 transition-colors">
                    <Mail className="w-3.5 h-3.5" /> Chase Emails
                  </button>
                  <button onClick={() => { handleSaveAsTemplate(); setShowMoreActions(false); }} disabled={savingTemplate}
                    className="w-full flex items-center gap-2 px-3 py-2 text-xs text-muted-foreground hover:text-foreground hover:bg-muted/30 transition-colors disabled:opacity-50">
                    <Save className="w-3.5 h-3.5" /> {savingTemplate ? 'Saving...' : 'Save Template'}
                  </button>
                  </>
                )}
                <button onClick={() => { fetchGapReport(); setShowMoreActions(false); }} data-testid="more-actions-gap-report"
                  className="w-full flex items-center gap-2 px-3 py-2 text-xs text-muted-foreground hover:text-foreground hover:bg-muted/30 transition-colors">
                  <BarChart3 className="w-3.5 h-3.5" /> Gap Report
                </button>
                <button onClick={() => { setShowDigestPrefs(true); setShowMoreActions(false); }}
                  data-testid="digest-prefs-menu-btn"
                  className="w-full flex items-center gap-2 px-3 py-2 text-xs text-muted-foreground hover:text-foreground hover:bg-muted/30 transition-colors">
                  <Mail className="w-3.5 h-3.5" /> Digest Preferences
                </button>
                <div className="border-t border-border my-1" />
                {isOwner && (
                <>
                <button onClick={() => { setHandoverReview('generate'); setShowMoreActions(false); }}
                  disabled={generatingHandover}
                  data-testid="generate-handover-btn"
                  className="w-full flex items-center gap-2 px-3 py-2 text-xs text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-950/20 transition-colors disabled:opacity-50">
                  <Package className="w-3.5 h-3.5" /> {generatingHandover ? 'Generating...' : 'Generate Handover'}
                </button>
                <button onClick={() => { setHandoverReview('email'); setShowMoreActions(false); }}
                  data-testid="email-handover-menu-btn"
                  className="w-full flex items-center gap-2 px-3 py-2 text-xs text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-950/20 transition-colors">
                  <Mail className="w-3.5 h-3.5" /> Email Handover Link
                </button>
                </>
                )}
                <button onClick={() => { navigate(`/process-map?project=${projectId}`); setShowMoreActions(false); }}
                  data-testid="open-workflow-guide-btn"
                  className="w-full flex items-center gap-2 px-3 py-2 text-xs text-muted-foreground hover:text-foreground hover:bg-muted/30 transition-colors">
                  <Route className="w-3.5 h-3.5" /> Workflow Guide
                </button>
                <button onClick={() => { setShowEmailGuide(true); setShowMoreActions(false); }}
                  data-testid="email-guide-menu-btn"
                  className="w-full flex items-center gap-2 px-3 py-2 text-xs text-muted-foreground hover:text-foreground hover:bg-muted/30 transition-colors">
                  <Mail className="w-3.5 h-3.5" /> Email Guide to Owner
                </button>
                {isOwner && (
                  <>
                  <div className="border-t border-border my-1" />
                  <button onClick={() => { handleDeleteProject(); setShowMoreActions(false); }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-xs text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-950/20 transition-colors">
                    <Trash2 className="w-3.5 h-3.5" /> Delete Project
                  </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {buildMsg && (() => {
        const stage = buildStatus?.stage || 'queued';
        const STAGE_LABEL = {
          queued: 'Starting…',
          extracting: 'Reading the drawings',
          importing: 'Building the equipment register',
          enriching: 'Finding warranties & maintenance',
          complete: 'Done',
          error: 'Error',
        };
        const et = buildStatus?.enriching_total || 0;
        const ed = buildStatus?.enriching_done || 0;
        let pct;
        if (stage === 'enriching' && et > 0) pct = 45 + Math.round(ed / et * 53);
        else pct = { queued: 3, extracting: 22, importing: 42, enriching: 45, complete: 100, error: 100 }[stage] ?? 10;
        const mm = Math.floor(buildElapsed / 60), ss = String(buildElapsed % 60).padStart(2, '0');
        const isErr = stage === 'error';
        return (
          <div className={`mb-6 px-4 py-3.5 rounded-md border ${isErr ? 'bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-900/40' : 'bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-900/40'}`} data-testid="header-build-progress">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                {!isErr && stage !== 'complete' && <Loader2 className="w-4 h-4 text-blue-600 dark:text-blue-400 animate-spin shrink-0" />}
                <span className={`text-xs font-heading uppercase tracking-wider ${isErr ? 'text-red-700 dark:text-red-300' : 'text-blue-700 dark:text-blue-300'}`} data-testid="build-stage-label">
                  {STAGE_LABEL[stage] || 'Working'}{stage === 'enriching' && et > 0 ? ` — ${ed}/${et} groups` : ''}
                </span>
              </div>
              <span className="text-[11px] font-mono text-muted-foreground tabular-nums" data-testid="build-elapsed">{mm}:{ss} elapsed</span>
            </div>
            {!isErr && (
              <div className="h-1.5 w-full bg-blue-100 dark:bg-blue-900/30 rounded-full overflow-hidden mb-2">
                <div className="h-full bg-blue-600 dark:bg-blue-400 rounded-full transition-[width] duration-700 ease-out" style={{ width: `${Math.min(pct, 100)}%` }} />
              </div>
            )}
            <p className={`text-[11px] ${isErr ? 'text-red-600 dark:text-red-400' : 'text-blue-600/80 dark:text-blue-300/70'}`}>{buildMsg}</p>
            {!isErr && stage !== 'complete' && (
              <p className="text-[10px] text-muted-foreground mt-1">Large drawing sets can take a few minutes — this bar keeps moving while it works. You can keep using the app; it runs in the background.</p>
            )}
          </div>
        );
      })()}
      {/* Summary Stats */}
      <div className="grid grid-cols-4 gap-4 mb-8">
        {(() => {
          const totalReq = divisions.reduce((s, d) => s + (d.total_required || 0), 0);
          const totalRcv = divisions.reduce((s, d) => s + (d.total_received || 0), 0);
          const folderPct = totalReq > 0 ? Math.round(totalRcv / totalReq * 100 * 10) / 10 : 0;
          return [
            { label: 'Divisions', value: divisions.length, accent: 'text-blue-600 dark:text-blue-400' },
            { label: 'Required Folders', value: totalReq, accent: 'text-foreground' },
            { label: 'Received', value: totalRcv, accent: 'text-emerald-600 dark:text-emerald-400' },
            { label: 'Completion', value: `${folderPct}%`, accent: folderPct >= 80 ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400' },
          ];
        })().map(s => (
          <div key={s.label} className="bg-card border border-border rounded-lg p-5 hover:border-primary/20 transition-colors">
            <p className="text-[10px] font-heading uppercase tracking-widest text-muted-foreground">{s.label}</p>
            <p className={`font-mono text-2xl font-bold mt-1.5 ${s.accent}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Workflow Stepper */}
      <WorkflowStepper
        projectId={projectId}
        refreshKey={stepperKey}
        onStepClick={(tab) => {
          setActiveTab(tab);
        }}
      />

      {/* Tab Bar — Process Order */}
      <div className="flex gap-1 mb-6 border-b border-border pb-2 overflow-x-auto">
        {[
          { id: 'intake', label: 'Intake', icon: Inbox },
          { id: 'assets', label: 'Assets & Equipment', icon: Package },
          { id: 'submittals', label: 'Submittals', icon: FileCheck2 },
          { id: 'floorplan', label: 'Floor Plans', icon: Building2 },
          { id: 'warranties', label: 'Warranties', icon: Shield },
          { id: 'contacts', label: 'Contacts', icon: Phone },
          { id: 'gates', label: 'Quality Gates', icon: CheckCircle2 },
          { id: 'audit', label: 'Audit', icon: ClipboardCheck },
          { id: 'team', label: 'Team', icon: Users },
        ].map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            data-testid={`tab-${t.id}`}
            className={`flex items-center gap-2 px-4 py-2 text-xs font-heading uppercase tracking-wider rounded-sm transition-colors whitespace-nowrap ${
              activeTab === t.id ? 'bg-blue-100 dark:bg-blue-900/20 text-primary border border-primary/30' : 'text-muted-foreground hover:text-foreground/80'
            }`}>
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>

      {activeTab === 'intake' ? (
        <>
          {/* Intake Sub-tabs */}
          <div className="flex gap-1 mb-4 flex-wrap">
            {[
              { id: 'upload', label: 'Upload & Extract', icon: Upload },
              { id: 'drawingsets', label: 'Drawing Sets', icon: Layers },
              { id: 'timeline', label: 'Timeline', icon: Clock },
              { id: 'history', label: 'History & Diffs', icon: FileSpreadsheet },
              { id: 'matrix', label: 'Division Matrix', icon: FolderOpen },
            ].map(st => (
              <button key={st.id} onClick={() => setIntakeSubTab(st.id)}
                data-testid={`intake-subtab-${st.id}`}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-heading uppercase tracking-wider rounded-sm transition-colors ${
                  intakeSubTab === st.id ? 'bg-blue-100 dark:bg-blue-900/20 text-primary border border-primary/30' : 'text-muted-foreground hover:text-foreground/80'
                }`}>
                <st.icon className="w-3 h-3" /> {st.label}
              </button>
            ))}
          </div>
          {intakeSubTab === 'upload' ? (
            <IntakePanel projectId={projectId} />
          ) : intakeSubTab === 'drawingsets' ? (
            <DrawingSetsPanel projectId={projectId} onViewOnFloorPlan={() => setActiveTab('floorplan')} />
          ) : intakeSubTab === 'timeline' ? (
            <TimelinePanel projectId={projectId} />
          ) : intakeSubTab === 'history' ? (
            <ExtractionHistoryPanel projectId={projectId} />
          ) : (
            /* Division Matrix */
            <>
              {projectDivisions.length > 0 && (
                <section className="mb-8">
                  <h2 className="text-xs font-heading uppercase tracking-widest text-primary/80 mb-3 flex items-center gap-2">
                    <FolderOpen className="w-4 h-4" /> Project-Level Divisions
                  </h2>
                  <div className="space-y-2">
                    {projectDivisions.map(d => (
                      <DivisionRow key={d.id} div={d} projectCode={project.project_code} onRefresh={fetchData} />
                    ))}
                  </div>
                </section>
              )}
              <section>
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-xs font-heading uppercase tracking-widest text-primary/80 flex items-center gap-2">
                    <FileText className="w-4 h-4" /> Trade Divisions ({tradeDivisions.length})
                  </h2>
                  {tradeDivisions.length > 0 && tradeDivisions.some(d => d.source === 'ai_inferred') && (
                    <p className="text-[10px] text-violet-600 dark:text-violet-400/70">
                      AI-inferred from drawings — import G703 to enrich with vendor &amp; contract data
                    </p>
                  )}
                  {tradeDivisions.length === 0 && (
                    <p className="text-xs text-muted-foreground">Extract drawings or import G703 to generate trade divisions</p>
                  )}
                </div>
                {tradeDivisions.length > 0 ? (
                  <div className="space-y-2">
                    {tradeDivisions.map(d => (
                      <DivisionRow key={d.id} div={d} projectCode={project.project_code} onRefresh={fetchData} />
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-40 bg-card border border-dashed border-border rounded-sm">
                    <Upload className="w-8 h-8 text-muted-foreground mb-2" />
                    <p className="text-sm text-muted-foreground mb-1">No trade divisions yet</p>
                    <p className="text-xs text-muted-foreground mb-3">Extract drawings to auto-generate, or import a G703</p>
                    <button onClick={() => setShowImport(true)}
                      className="px-4 py-2 text-xs font-heading uppercase tracking-wider bg-primary/10 text-primary border border-primary/30 rounded-sm hover:bg-primary/15 transition-colors">
                      Import G703
                    </button>
                  </div>
                )}
              </section>
            </>
          )}
        </>
      ) : activeTab === 'assets' ? (
        <AssetPanel projectId={projectId} />
      ) : activeTab === 'submittals' ? (
        <SubmittalPanel projectId={projectId} />
      ) : activeTab === 'floorplan' ? (
        <FloorPlanPanel
          projectId={projectId}
          onGoToDrawingSets={() => { setActiveTab('intake'); setIntakeSubTab('drawingsets'); }}
        />
      ) : activeTab === 'warranties' ? (
        <>
          {/* Warranty Sub-tabs */}
          <div className="flex gap-1 mb-4">
            {[
              { id: 'documents', label: 'Documents & O&M', icon: FileText },
              { id: 'calendar', label: 'Calendar', icon: Calendar },
            ].map(st => (
              <button key={st.id} onClick={() => setWarrantySubTab(st.id)}
                data-testid={`warranty-subtab-${st.id}`}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-heading uppercase tracking-wider rounded-sm transition-colors ${
                  warrantySubTab === st.id ? 'bg-blue-100 dark:bg-blue-900/20 text-primary border border-primary/30' : 'text-muted-foreground hover:text-foreground/80'
                }`}>
                <st.icon className="w-3 h-3" /> {st.label}
              </button>
            ))}
          </div>
          {warrantySubTab === 'documents' ? (
            <WarrantyPanel projectId={projectId} />
          ) : (
            <WarrantyCalendar projectId={projectId} substantialCompletion={project.substantial_completion} />
          )}
        </>
      ) : activeTab === 'contacts' ? (
        <ServiceContactsPanel projectId={projectId} />
      ) : activeTab === 'gates' ? (
        <QualityGates projectId={projectId} />
      ) : activeTab === 'audit' ? (
        <AuditPanel projectId={projectId} />
      ) : activeTab === 'team' ? (
        <>
          {/* Team Sub-tabs */}
          <div className="flex gap-1 mb-4">
            {[
              { id: 'team', label: 'Project Team', icon: Users },
              { id: 'stakeholders', label: 'Stakeholders', icon: Users },
              { id: 'trade-portal', label: 'Trade Portal', icon: Link2 },
            ].map(st => (
              <button key={st.id} onClick={() => setTeamSubTab(st.id)}
                data-testid={`team-subtab-${st.id}`}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-heading uppercase tracking-wider rounded-sm transition-colors ${
                  teamSubTab === st.id ? 'bg-blue-100 dark:bg-blue-900/20 text-primary border border-primary/30' : 'text-muted-foreground hover:text-foreground/80'
                }`}>
                <st.icon className="w-3 h-3" /> {st.label}
              </button>
            ))}
          </div>
          {teamSubTab === 'team' ? (
            <TeamPanel projectId={projectId} userRole={project.user_role || 'owner'} />
          ) : teamSubTab === 'trade-portal' ? (
            <TradePortalManager projectId={projectId} />
          ) : (
            <StakeholderPanel projectId={projectId} divisions={divisions} />
          )}
        </>
      ) : null}

      {/* Gap Report Slide-over */}
      {showGapReport && gapReport && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="gap-report-panel">
          <div className="absolute inset-0 bg-black/60" onClick={() => setShowGapReport(false)} />
          <div className="relative w-[600px] bg-card border-l border-border overflow-y-auto">
            <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between z-10">
              <div>
                <h2 className="font-heading text-lg font-bold text-foreground">Gap Report</h2>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {gapReport.total_received}/{gapReport.total_required} received — {gapReport.completion_pct}% complete
                </p>
              </div>
              <button onClick={() => setShowGapReport(false)} className="text-muted-foreground hover:text-foreground/80">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-2">
              {gapReport.gaps.length === 0 ? (
                <div className="text-center py-12">
                  <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
                  <p className="text-emerald-600 dark:text-emerald-400 font-heading">All items received!</p>
                </div>
              ) : gapReport.gaps.map((g, i) => (
                <div key={i} className="flex items-center gap-3 p-3 bg-card border border-border rounded-sm">
                  <AlertTriangle className={`w-4 h-4 shrink-0 ${g.status === 'missing' ? 'text-red-500' : 'text-amber-500'}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-foreground/80 truncate">{g.csi_number} — {g.trade_name}</p>
                    <p className="text-xs text-muted-foreground">Folder {g.folder_letter}: {g.folder_name}</p>
                  </div>
                  <span className={`text-[10px] font-mono px-2 py-0.5 rounded-sm ${g.status === 'missing' ? 'bg-red-100 dark:bg-red-950/30 text-red-700 dark:text-red-400' : 'bg-amber-100 dark:bg-amber-950/30 text-amber-700 dark:text-amber-400'}`}>
                    {g.status}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* G703 Import Modal */}
      {showImport && (
        <G703ImportModal
          projectId={projectId}
          onClose={() => setShowImport(false)}
          onImported={() => { setShowImport(false); fetchData(); }}
        />
      )}

      {/* Applicability Wizard */}
      {showWizard && (
        <ApplicabilityWizard
          projectId={projectId}
          divisions={divisions}
          onClose={() => setShowWizard(false)}
          onComplete={() => { setShowWizard(false); fetchData(); }}
        />
      )}

      {/* Chase Email Panel */}
      {showChaseEmails && (
        <ChaseEmailPanel
          projectId={projectId}
          onClose={() => setShowChaseEmails(false)}
        />
      )}

      {/* Document Upload Modal */}
      {showDocUpload && (
        <DocumentUploadPanel
          projectId={projectId}
          divisions={divisions}
          onUploaded={fetchData}
          onClose={() => setShowDocUpload(false)}
        />
      )}

      {/* Procore Sync Modal */}
      {showProcore && (
        <ProcoreSyncPanel
          projectId={projectId}
          onImported={fetchData}
          onClose={() => setShowProcore(false)}
        />
      )}

      {/* Export Handoff Panel */}
      {showExport && exportPreview && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="export-handoff-panel">
          <div className="absolute inset-0 bg-black/60" onClick={() => setShowExport(false)} />
          <div className="relative w-[520px] bg-card border-l border-border overflow-y-auto">
            <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between z-10">
              <div>
                <h2 className="font-heading text-lg font-bold text-foreground">Building Turnover Package</h2>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Export the complete handoff package for owner/FM
                </p>
              </div>
              <button onClick={() => setShowExport(false)} className="text-muted-foreground hover:text-foreground/80">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Package Summary */}
              <div className="space-y-3">
                <p className="text-[10px] font-heading uppercase tracking-widest text-muted-foreground">Package Contents</p>
                <div className="grid grid-cols-3 gap-3">
                  <div className="text-center p-3 bg-secondary/30 rounded-sm border border-border">
                    <p className="font-mono text-xl font-bold text-emerald-600 dark:text-emerald-400">{exportPreview.asset_summary?.total_active || 0}</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">Active Assets</p>
                  </div>
                  <div className="text-center p-3 bg-secondary/30 rounded-sm border border-border">
                    <p className="font-mono text-xl font-bold text-blue-600 dark:text-blue-400">{exportPreview.warranty_count || 0}</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">Warranties</p>
                  </div>
                  <div className="text-center p-3 bg-secondary/30 rounded-sm border border-border">
                    <p className="font-mono text-xl font-bold text-amber-600 dark:text-amber-400">{exportPreview.division_count || 0}</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">Divisions</p>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="text-center p-3 bg-secondary/30 rounded-sm border border-border">
                    <p className="font-mono text-xl font-bold text-violet-600 dark:text-violet-400">{exportPreview.stakeholder_count || 0}</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">Stakeholders</p>
                  </div>
                  <div className="text-center p-3 bg-secondary/30 rounded-sm border border-border">
                    <p className="font-mono text-xl font-bold text-rose-600 dark:text-rose-400">{exportPreview.change_order_count || 0}</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">Change Orders</p>
                  </div>
                  <div className="text-center p-3 bg-secondary/30 rounded-sm border border-border">
                    <p className={`font-mono text-xl font-bold ${exportPreview.completion_pct >= 80 ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'}`}>
                      {exportPreview.completion_pct}%
                    </p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">Doc Completion</p>
                  </div>
                </div>
              </div>

              {/* Gap Warning */}
              {exportPreview.gap_count > 0 && (
                <div className="flex items-start gap-2 p-3 bg-amber-100 dark:bg-amber-950/20 border border-amber-300 dark:border-amber-900/30 rounded-sm">
                  <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-500 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs text-amber-700 dark:text-amber-400 font-medium">{exportPreview.gap_count} documentation gaps remain</p>
                    <p className="text-[10px] text-amber-600 mt-0.5">The export will include a "Remaining Gaps" section listing all missing items.</p>
                  </div>
                </div>
              )}

              {/* Export Formats */}
              <div className="space-y-3">
                <p className="text-[10px] font-heading uppercase tracking-widest text-muted-foreground">Choose Format</p>

                {/* PDF */}
                <button onClick={() => handleExport('pdf')} disabled={!!exporting} data-testid="export-pdf-btn"
                  className="w-full flex items-center gap-4 p-4 bg-card border border-border hover:border-primary/30/60 rounded-sm transition-colors text-left disabled:opacity-50">
                  <div className="w-10 h-10 rounded-sm bg-red-100 dark:bg-red-950/30 border border-red-300 dark:border-red-900/30 flex items-center justify-center">
                    {exporting === 'pdf' ? <Loader2 className="w-5 h-5 text-red-600 dark:text-red-400 animate-spin" /> : <FileText className="w-5 h-5 text-red-600 dark:text-red-400" />}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-foreground">PDF Report</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">
                      Full closeout report: cover sheet, asset registry, warranties, division matrix, gaps, stakeholders, change orders
                    </p>
                  </div>
                  <Download className="w-4 h-4 text-muted-foreground" />
                </button>

                {/* Excel */}
                <button onClick={() => handleExport('excel')} disabled={!!exporting} data-testid="export-excel-btn"
                  className="w-full flex items-center gap-4 p-4 bg-card border border-border hover:border-primary/30/60 rounded-sm transition-colors text-left disabled:opacity-50">
                  <div className="w-10 h-10 rounded-sm bg-emerald-100 dark:bg-emerald-950/30 border border-emerald-300 dark:border-emerald-900/30 flex items-center justify-center">
                    {exporting === 'excel' ? <Loader2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400 animate-spin" /> : <FileSpreadsheet className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-foreground">IWMS Import Workbook</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">
                      6-sheet Excel: Asset Registry, Warranties, Division Matrix, Stakeholders, Remaining Gaps, Change Orders
                    </p>
                  </div>
                  <Download className="w-4 h-4 text-muted-foreground" />
                </button>

                {/* ZIP */}
                <button onClick={() => handleExport('zip')} disabled={!!exporting} data-testid="export-zip-btn"
                  className="w-full flex items-center gap-4 p-4 bg-card border border-primary/30 hover:border-blue-500/50 rounded-sm transition-colors text-left disabled:opacity-50">
                  <div className="w-10 h-10 rounded-sm bg-primary/10 border border-primary/30 flex items-center justify-center">
                    {exporting === 'zip' ? <Loader2 className="w-5 h-5 text-primary animate-spin" /> : <FileArchive className="w-5 h-5 text-primary" />}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-primary">Complete Turnover Package</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">
                      PDF + Excel + Cx doc index + warranty cert index + manifest + README — ready for IWMS import
                    </p>
                  </div>
                  <Download className="w-4 h-4 text-primary" />
                </button>
              </div>

              {/* IWMS Note */}
              <div className="p-3 bg-secondary/20 rounded-sm border border-border/50">
                <p className="text-[10px] text-muted-foreground leading-relaxed">
                  The Excel workbook is formatted for direct import into IWMS/CAFM platforms
                  (FMiQ, Archibus, Tririga, Planon, etc.). The ZIP package includes machine-readable
                  JSON manifests for automated ingestion.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
      {showDigestPrefs && (
        <DigestPreferences onClose={() => setShowDigestPrefs(false)} />
      )}

      {showEmailGuide && (
        <EmailGuideModal projectId={projectId} onClose={() => setShowEmailGuide(false)} />
      )}

      {showEmailHandover && (
        <EmailGuideModal
          projectId={projectId}
          onClose={() => setShowEmailHandover(false)}
          title="Email Handover Link"
          description="Sends a secure link to the owner Handover Portal (equipment, warranties, O&M, floor plan, contacts & downloads). Pick a saved contact or type any email."
          endpoint={`/closeout/projects/${projectId}/email-handover`}
          showEdition={false}
          successNoun="handover"
        />
      )}

      {handoverReview && (
        <HandoverReviewModal
          projectId={projectId}
          mode={handoverReview === 'email' ? 'pre-email' : 'pre-generate'}
          proceedLabel={handoverReview === 'email' ? 'Email anyway' : 'Generate anyway'}
          onProceed={() => {
            if (handoverReview === 'email') setShowEmailHandover(true);
            else handleGenerateHandover();
          }}
          onClose={() => setHandoverReview(null)}
        />
      )}
    </div>
  );
}
